import isPersonalEmail from "@brexhq/next/src/utils/isPersonalEmail";
import {
  EmployeeCountV3,
  FundingSourceV2,
  LeadSelfAttestedType,
  QuestionFundingSource,
  SalesSegment,
  SalesVolumeV2,
  QuestionEmployeeCount,
  CompanyTypeV3,
  SalesVolumeV3,
} from "@brexhq/next/types/brex-api";

import { employeeCountOptionsOBS4 } from "../helpers/leadFormFields";

import {
  leadFormMachineContext,
  LeadFormSteps,
  UpdateLeadResponse,
} from "./types";

/**
 *
 * @param prev previous work email set
 * @param current user came back to the email step and submitted the same email
 * (aka the form was left unchanged)
 * @returns true if sam, false if not
 */
export const sameWorkEmail = (
  prev: string | undefined,
  current: string | undefined,
) => {
  return prev === current;
};

/**
 *
 * @param formValues form values filled in so far
 * @returns whether or not user filled in the userInfo step
 */
export const isUserInfoFilled = (formValues: leadFormMachineContext) => {
  return (
    formValues.firstName &&
    formValues.lastName &&
    formValues.companyName &&
    formValues.phone
  );
};

/**
 *
 * @param fundingSources list of funding sources (multiselect for M7)
 * @returns if any of the options have professional investment
 */
export const isPIV2 = (fundingSources?: FundingSourceV2[]) => {
  if (!fundingSources) return false;
  return (
    fundingSources?.includes(FundingSourceV2.ANGEL_INVESTMENT) ||
    fundingSources?.includes(FundingSourceV2.VENTURE_ACCELERATOR_GROWTH) ||
    fundingSources?.includes(FundingSourceV2.PRIVATE_EQUITY)
  );
};

export const hasProfessionalInvestment = (
  fundingSource?: QuestionFundingSource,
) => {
  return fundingSource === QuestionFundingSource.PROFESSIONAL_INVESTMENT;
};

export const isEcomm = (companyType?: LeadSelfAttestedType | CompanyTypeV3) => {
  return (
    companyType === LeadSelfAttestedType.ECOMMERCE ||
    companyType === CompanyTypeV3.ECOMMERCE
  );
};

export const isVBOrPIMMSalesSegment = (
  salesSegment: SalesSegment | undefined,
) => {
  if (!salesSegment) return false;
  return (
    salesSegment === SalesSegment.VENTURE_BACKED ||
    salesSegment === SalesSegment.PI_MID_MARKET ||
    salesSegment === SalesSegment.ECOMM_MID_MARKET
  );
};

export const isSmallBusinessSegment = (salesSegment: SalesSegment) => {
  if (!salesSegment) return false;
  return salesSegment === SalesSegment.SMALL_BUSINESS;
};

export const areMonthlySalesUnder400K = (
  monthlySalesVolume?: SalesVolumeV2 | SalesVolumeV3,
) => {
  return (
    monthlySalesVolume === SalesVolumeV2.UNDER_FOUR_HUNDRED_K ||
    monthlySalesVolume === SalesVolumeV3.UNDER_FOUR_HUNDRED_K
  );
};

export const employeeGreaterThan50 = (
  employeeCount?: QuestionEmployeeCount | EmployeeCountV3,
) => {
  return (
    employeeCount === employeeCountOptionsOBS4[3].value ||
    employeeCount === employeeCountOptionsOBS4[4].value ||
    employeeCount === employeeCountOptionsOBS4[5].value
  );
};

const getSegUnidentifiedProgress = () => {
  return [
    `${LeadFormSteps.segUnidentified}.${LeadFormSteps.employeeCount}`,
    `${LeadFormSteps.segUnidentified}.${LeadFormSteps.ecommerce}`,
    `${LeadFormSteps.segUnidentified}.${LeadFormSteps.monthlySales}`,
    `${LeadFormSteps.segUnidentified}.${LeadFormSteps.fundingSources}`,
  ];
};
const getPIMMVBProgress = () => {
  return [
    `${LeadFormSteps.segVBOrPIMM}.${LeadFormSteps.ecommerce}`,
    `${LeadFormSteps.segVBOrPIMM}.${LeadFormSteps.monthlySales}`,
  ];
};
//Empty for now since we take them directly to the end state
const getMMPlusProgress = () => {
  return [];
};

export const getSequence = (ctx: leadFormMachineContext) => {
  const sequence: string[] = [LeadFormSteps.userInfo, LeadFormSteps.website];
  if (!ctx.salesSegment) {
    sequence.push(...getSegUnidentifiedProgress());
  } else if (isVBOrPIMMSalesSegment(ctx.salesSegment)) {
    sequence.push(...getPIMMVBProgress());
  } else {
    //MM Plus, we have a different flow for personal email
    if (isPersonalEmail(ctx.workEmail || "")) {
      sequence.push(...getMMPlusProgress());
    }
    sequence.push(...getMMPlusProgress());
  }
  return sequence;
};

export const getStepProgress = (
  ctx: leadFormMachineContext,
  currentStep: string,
) => {
  const sequence = getSequence(ctx);
  return sequence.indexOf(currentStep) / sequence.length;
};

/**
 * OBS 4 segments
 */
export const employeeLessThan50 = (employeeCount?: EmployeeCountV3) => {
  return (
    employeeCount === EmployeeCountV3.ONE_TO_FOUR ||
    employeeCount === EmployeeCountV3.FIVE_TO_TWENTY ||
    employeeCount === EmployeeCountV3.TWENTY_ONE_TO_FIFTY
  );
};
export const employeeGreaterThan250 = (employeeCount?: EmployeeCountV3) => {
  return (
    employeeCount === EmployeeCountV3.TWO_HUNDRED_FIFTY_ONE_TO_ONE_THOUSAND ||
    employeeCount === EmployeeCountV3.MORE_THAN_ONE_THOUSAND
  );
};
export const employeeBetween51and250 = (employeeCount?: EmployeeCountV3) => {
  return employeeCount === EmployeeCountV3.FIFTY_ONE_TO_TWO_HUNDRED_FIFTY;
};

export const isGrowthTier1Plus = (salesSegment: SalesSegment) => {
  if (!salesSegment) return false;
  return (
    salesSegment === SalesSegment.GROWTH_TIER_ONE ||
    salesSegment === SalesSegment.MID_MARKET ||
    salesSegment === SalesSegment.ENTERPRISE
  );
};

export const isGrowthTier1 = (salesSegment: SalesSegment) => {
  if (!salesSegment) return false;
  return salesSegment === SalesSegment.GROWTH_TIER_ONE;
};

export const isMidMarket = (salesSegment: SalesSegment) => {
  if (!salesSegment) return false;
  return salesSegment === SalesSegment.MID_MARKET;
};

export const isEnterprise = (salesSegment: SalesSegment) => {
  if (!salesSegment) return false;
  return salesSegment === SalesSegment.ENTERPRISE;
};

export const isGrowthTier2 = (salesSegment: SalesSegment) => {
  if (!salesSegment) return false;
  return salesSegment === SalesSegment.GROWTH_TIER_TWO;
};

export const isESTier2 = (salesSegment: SalesSegment) => {
  if (!salesSegment) return false;
  return salesSegment === SalesSegment.EARLY_STAGE_TIER_TWO;
};
export const isESTier1 = (salesSegment: SalesSegment) => {
  if (!salesSegment) return false;
  return salesSegment === SalesSegment.EARLY_STAGE_TIER_ONE;
};

export const isEcommES = (salesSegment: SalesSegment) => {
  if (!salesSegment) return false;
  return salesSegment === SalesSegment.EARLY_STAGE_ECOMM;
};
/**
 *
 * @param context context of the state machine, mainly using this for checking if personal email
 * @param data response data from the updateLead mutation
 * @returns must match the following to be sent to password:
 * 1. need a sales segment
 * 2. if you're not an enterprise segment
 * 3. if you have a personal email
 * 4. if referral code is spotnana_marriott
 */
export const segUnidentifiedSendToPasswordScreen = (
  context: leadFormMachineContext,
  data: UpdateLeadResponse,
) => {
  if (data.salesSegment) {
    return (
      !isEnterprise(data.salesSegment) ||
      isPersonalEmail(context.workEmail || "") ||
      isReferralPartner(context.refCode)
    );
  }
  return false;
};

/**
 *
 * @param data the response values from the updateLead mutation call
 *  you can find this in useLeadFormMachine.tsx
 * @returns
 * 1. Must have a sales segment
 * 2. Must be either Growth 2, Growth 1, Mid market, or enterprise
 * 3. Otherwise you're not eligible for sales
 */
export const segUnidentifiedSendToSuccess = (data: UpdateLeadResponse) => {
  if (data.salesSegment) {
    return (
      isGrowthTier2(data.salesSegment) || isGrowthTier1Plus(data.salesSegment)
    );
  }
  return false;
};

export const isReferralPartner = (refCode?: string) => {
  return refCode === "spotnana_marriott";
};
